import { File as FileIcon, PauseCircle, RefreshCw, Trash2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import IconButton from '../IconButton';

export interface SelectedFileDisplayProps {
    file: File | FileInfo;
    fileExtendInfo?: string;
    isQueued?: boolean;
    progress?: number; // 0 to 100
    onReUpload?: (() => void) | undefined;
    onRemove?: (() => void) | undefined;
    onPause?: () => void;
    onCancel?: () => void;
    onClick?: () => void;
}
export interface FileInfo {
    name: string;
    size: number;
}
const SelectedFileDisplay = ({
    file,
    fileExtendInfo,
    isQueued,
    progress,
    onReUpload,
    onRemove,
    onPause,
    onCancel,
    onClick,
}: SelectedFileDisplayProps) => {
    const { t } = useTranslation('common');
    return (
        <div
            className="bg-panel mb-3 flex w-full flex-col rounded-[10px] px-4 py-3"
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
                if ((e.key === 'Enter' || e.key === ' ') && onClick) {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            <div className="flex items-center justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    <FileIcon className="size-5 shrink-0 text-teal-600" />
                    <div className="flex flex-col">
                        <span className="block leading-6 font-bold break-all whitespace-normal text-neutral-900">
                            {file.name}
                        </span>
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {onReUpload && (
                        <button
                            type="button"
                            className="ml-2 pl-12 text-blue-600 hover:text-blue-500"
                            title={t('common:reupload')}
                            onClick={onReUpload ?? undefined}
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>
                    )}
                    {onRemove && (
                        <button
                            type="button"
                            className="text-destructive hover:text-destructive/80 ml-1"
                            title={t('common:remove')}
                            onClick={onRemove ?? undefined}
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    )}
                    {onPause && (
                        <IconButton
                            aria-label={t('pendingUploads.pause')}
                            title={t('pendingUploads.pause')}
                            onClick={e => {
                                e.stopPropagation();
                                onPause?.();
                            }}
                            icon={<PauseCircle className="h-5 w-5 text-yellow-500" />}
                        />
                    )}
                    {onCancel && (
                        <IconButton
                            aria-label={t('pendingUploads.cancel')}
                            title={t('pendingUploads.cancel')}
                            onClick={e => {
                                e.stopPropagation();
                                onCancel?.();
                            }}
                            icon={<XCircle className="text-destructive h-5 w-5" />}
                        />
                    )}
                    {isQueued && (
                        <span className="text-muted-foreground rounded bg-neutral-100 px-2 py-0.5 text-xs">
                            {t('pendingUploads.queued')}
                        </span>
                    )}
                </div>
            </div>
            <span className="text-muted-foreground pl-8 leading-6 font-medium">
                {t('common:fileSize', {
                    size: (file.size / 1024 / 1024).toFixed(2),
                })}
                {fileExtendInfo && (
                    <>
                        <span className="mx-1">|</span>
                        <span>{fileExtendInfo}</span>
                    </>
                )}
            </span>
            {typeof progress === 'number' && progress >= 0 && (
                <div className="mt-2 flex items-center gap-2 pl-8">
                    <div className="relative h-2 flex-1 rounded-full bg-neutral-200">
                        <div
                            className="absolute top-0 left-0 h-2 rounded-full bg-teal-500 transition-all"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="text-muted-foreground text-xs font-semibold">
                        {t('pendingUploads.progress', { percent: progress.toFixed(2) })}
                    </span>
                </div>
            )}
        </div>
    );
};

export default SelectedFileDisplay;
